import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Grid, Plus, Loader2, X, Trash2, Edit3, Save, User, MapPin,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATE_BADGES = {
  never_visited: 'bg-gray-100 text-gray-700 border-gray-200',
  building_presence: 'bg-amber-50 text-amber-800 border-amber-200',
  relationships_active: 'bg-green-50 text-green-800 border-green-200',
  resting: 'bg-blue-50 text-blue-800 border-blue-200',
};

const fmtRange = (s) => {
  if (s.range_start && s.range_end) return `${s.range_start} → ${s.range_end}`;
  if (s.range_start) return `from ${s.range_start}`;
  if (s.range_end) return `up to ${s.range_end}`;
  return '';
};

const SegmentFormModal = ({ open, initial, onClose, onSave, stateLabels }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    label: '', range_start: '', range_end: '', notes: '',
    assigned_volunteer_label: '', is_active: true, coverage_state: 'never_visited',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        label: initial?.label || '',
        range_start: initial?.range_start || '',
        range_end: initial?.range_end || '',
        notes: initial?.notes || '',
        assigned_volunteer_label: initial?.assigned_volunteer_label || '',
        is_active: initial?.is_active !== false,
        coverage_state: initial?.coverage_state || 'never_visited',
      });
    }
  }, [open, initial]);

  if (!open) return null;

  const handleSave = async () => {
    if (!form.label.trim()) return;
    setSaving(true);
    try { await onSave(form, initial?.id); onClose(); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="segment-form-modal">
      <div className="bg-white rounded-lg max-w-md w-full p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-base font-bold text-[#0A0A0A]">
            {isEdit ? 'Edit Segment' : 'New Segment'}
          </h3>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#0A0A0A]" data-testid="segment-form-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[11px] text-[#4B5563]">
          Segments help volunteers stay organized along a street — they&apos;re for keeping crews
          working in different blocks at the same time. They are <em>not</em> a list of houses to canvass.
        </p>
        <div className="space-y-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Label *</label>
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Segment A"
              className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none"
              data-testid="segment-label-input" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Range start</label>
              <input value={form.range_start} onChange={(e) => setForm({ ...form, range_start: e.target.value })}
                placeholder="221-01"
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="segment-range-start" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Range end</label>
              <input value={form.range_end} onChange={(e) => setForm({ ...form, range_end: e.target.value })}
                placeholder="221-40"
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="segment-range-end" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Assigned volunteer / crew (optional)</label>
            <input value={form.assigned_volunteer_label}
              onChange={(e) => setForm({ ...form, assigned_volunteer_label: e.target.value })}
              placeholder="Crew 1 — Maria & Jordan"
              className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none"
              data-testid="segment-assigned-input" />
            <p className="text-[10px] text-[#9CA3AF] mt-0.5">
              Soft assignment — any volunteer can still help here if needed.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Status</label>
              <select value={form.coverage_state}
                onChange={(e) => setForm({ ...form, coverage_state: e.target.value })}
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="segment-state-select">
                {Object.entries(stateLabels || {}).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-xs text-[#0A0A0A] cursor-pointer pb-1">
                <input type="checkbox" checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  data-testid="segment-active-toggle" />
                Currently being worked
              </label>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Notes (internal)</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              placeholder="Context for the crew — e.g. apartment buildings between Hendrix & Linden."
              className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none resize-none"
              data-testid="segment-notes-input" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-[#E5E7EB]">
          <button onClick={onClose} className="text-xs px-3 py-1.5 text-[#4B5563] hover:bg-[#F8F9FA] rounded">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.label.trim()}
            className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded hover:bg-[#9F1421] disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="segment-save-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            {isEdit ? 'Save' : 'Create segment'}
          </button>
        </div>
      </div>
    </div>
  );
};

const SegmentsPanel = ({ streetId, canWrite = true }) => {
  const [segments, setSegments] = useState([]);
  const [stateLabels, setStateLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    if (!streetId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/outreach/streets/${streetId}/segments`,
        { withCredentials: true });
      setSegments(r.data?.segments || []);
      setStateLabels(r.data?.coverage_state_labels || {});
    } finally { setLoading(false); }
  }, [streetId]);

  useEffect(() => { load(); }, [load]);

  const save = async (form, id) => {
    if (id) {
      await axios.put(`${BACKEND_URL}/api/admin/outreach/segments/${id}`, form, { withCredentials: true });
    } else {
      await axios.post(`${BACKEND_URL}/api/admin/outreach/streets/${streetId}/segments`, form,
        { withCredentials: true });
    }
    await load();
  };

  const remove = async (seg) => {
    if (!window.confirm(`Delete segment "${seg.label}"? This won't delete any logs.`)) return;
    await axios.delete(`${BACKEND_URL}/api/admin/outreach/segments/${seg.id}`, { withCredentials: true });
    await load();
  };

  return (
    <div className="mt-2" data-testid={`segments-panel-${streetId}`}>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] inline-flex items-center gap-1.5">
          <Grid className="w-3 h-3" /> Segments ({segments.length})
        </p>
        {canWrite && (
          <button onClick={() => { setEditing(null); setShowForm(true); }}
            className="text-[10px] px-2 py-0.5 bg-[#0A0A0A] text-white rounded hover:bg-[#333] inline-flex items-center gap-1"
            data-testid={`segment-new-btn-${streetId}`}>
            <Plus className="w-2.5 h-2.5" /> Segment
          </button>
        )}
      </div>
      {loading ? (
        <Loader2 className="w-3 h-3 animate-spin text-[#BA1826]" />
      ) : segments.length === 0 ? (
        <p className="text-[11px] text-[#9CA3AF] italic">No segments yet — optional. Add one if the street is long enough to need crews split into blocks.</p>
      ) : (
        <ul className="space-y-1">
          {segments.map((s) => (
            <li key={s.id}
              className={`bg-white border rounded px-2 py-1.5 text-xs ${s.is_active ? 'border-[#E5E7EB]' : 'border-[#F1F5F9] opacity-60'}`}
              data-testid={`segment-row-${s.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#0A0A0A] inline-flex items-center gap-1.5 flex-wrap">
                    {s.label}
                    {fmtRange(s) && (
                      <span className="text-[10px] text-[#4B5563] font-normal inline-flex items-center gap-1">
                        <MapPin className="w-2.5 h-2.5" />{fmtRange(s)}
                      </span>
                    )}
                    <span className={`text-[9px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded ${STATE_BADGES[s.coverage_state] || STATE_BADGES.never_visited}`}>
                      {stateLabels[s.coverage_state] || s.coverage_state}
                    </span>
                    {!s.is_active && (
                      <span className="text-[9px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 border-gray-200">Inactive</span>
                    )}
                  </p>
                  {s.assigned_volunteer_label && (
                    <p className="text-[10px] text-[#4B5563] inline-flex items-center gap-1 mt-0.5">
                      <User className="w-2.5 h-2.5" /> {s.assigned_volunteer_label}
                    </p>
                  )}
                  {s.notes && <p className="text-[10px] text-[#9CA3AF] italic">{s.notes}</p>}
                </div>
                {canWrite && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => { setEditing(s); setShowForm(true); }}
                      className="p-1 text-[#4B5563] hover:text-[#0A0A0A]" data-testid={`segment-edit-${s.id}`}>
                      <Edit3 className="w-3 h-3" />
                    </button>
                    <button onClick={() => remove(s)}
                      className="p-1 text-red-600 hover:text-red-800" data-testid={`segment-delete-${s.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <SegmentFormModal open={showForm} initial={editing} stateLabels={stateLabels}
        onClose={() => { setShowForm(false); setEditing(null); }}
        onSave={save} />
    </div>
  );
};

export default SegmentsPanel;
